/**
 * Which axios requests carry the signed-in user's Firebase ID token — the pure rule.
 *
 * ── Why (request-auth phase 0, 2026-10-17) ───────────────────────────────────────────────────────
 * Login gives the browser a Firebase session whose token says who the user is, but only ~a dozen call
 * sites send it; the other ~430 axios calls send nothing, so the API cannot tell who is calling.
 * Phase 0 attaches the token to our own API calls, with NO backend change: every handler that reads
 * the header today either requires it (its call site already sets one, which we never touch) or treats
 * a bad token exactly like a missing one. Design + decisions: teamio-serverless-api
 * docs/plans/2026-10-17-request-auth-design.md (feature/demo-site), decisions 21a / T4a.
 *
 * ── The rule — attach ONLY when every one holds, otherwise the request goes out exactly as today ───
 *   • a Firebase user is signed in right now, and we hold an unexpired token for them
 *   • the request goes to OUR API: the exact env base, or a path under it — never S3 presigned
 *     uploads (S3 rejects an Authorization header), Cloudinary, blob:/data:, /version.json, or a
 *     look-alike host (`<API>.evil.com`)
 *   • not a login route (google-login, microsoft-login, testLogin)
 *   • the browser ALREADY preflights it, so the header adds no round trip ("nothing slower"):
 *     PUT / PATCH / DELETE always; POST only when axios sends the body as JSON (a plain object or
 *     array, no caller-set non-JSON Content-Type). FormData / string / empty POSTs are "simple"
 *     requests today — a header would add an OPTIONS round trip each (phase 5 decision).
 *   • GET: ONLY a route the API gates for signed-in staff (STAFF_GET_ROUTES, generated from the API's
 *     own route inventory — scripts/gen-staff-get-routes.js). Saumil chose this 2026-10-19 (P3 decision
 *     2a) and accepted the cost: one cheap OPTIONS (API Gateway answers it, no Lambda) per distinct URL.
 *     Every other GET — public, oauth, health, not yet wrapped — still goes out exactly as today.
 *   • the caller has not set an Authorization header itself (any case, any level) — theirs wins.
 *
 * ── Purity ───────────────────────────────────────────────────────────────────────────────────────
 * Pure on purpose (its one import is the generated route list, a plain data file): cypress/node-tests/
 * auth-header-policy.test.js executes THIS file (and runs it through the real axios 0.18 pipeline). The wiring — Firebase listener + axios registration — lives in
 * src/plugins/authTokenInterceptor.js. Same split as phiMask.js / phiMaskInterceptor.js.
 */

import { STAFF_GET_ROUTES } from '@/utils/staffGetRoutes';

export const LOGIN_PATHS = ['google-login', 'microsoft-login', 'testlogin'];

// Never attach to a token that expires within this window — Firebase refreshes it in the background
// ~5 min before expiry, so a token this close to the end is one Firebase is about to replace.
export const EXPIRY_SKEW_MS = 60 * 1000;

const ABSOLUTE_URL = /^([a-z][a-z\d+\-.]*:)?\/\//i; // axios 0.18's isAbsoluteURL

/** The env API base with trailing slashes removed; '' when missing or not an http(s) URL. */
export function normalizeApiBase(raw) {
  if (typeof raw !== 'string') return '';
  const base = raw.trim().replace(/\/+$/, '');
  return /^https?:\/\/[^/?#]+/i.test(base) ? base : '';
}

function hasHeader(obj, lowerName) {
  if (!obj || typeof obj !== 'object') return false;
  return Object.keys(obj).some((k) => k.toLowerCase() === lowerName);
}

function headerValue(obj, lowerName) {
  if (!obj || typeof obj !== 'object') return undefined;
  const key = Object.keys(obj).find((k) => k.toLowerCase() === lowerName);
  return key === undefined ? undefined : obj[key];
}

/** Caller already chose an Authorization header — per request, or on axios defaults (common / per-method). */
function callerSetAuthorization(headers, method) {
  if (!headers || typeof headers !== 'object') return false;
  if (hasHeader(headers, 'authorization')) return true;
  if (hasHeader(headers.common, 'authorization')) return true;
  return hasHeader(headers[method], 'authorization');
}

function isPlainJsonBody(data) {
  if (Array.isArray(data)) return true;
  if (data === null || typeof data !== 'object') return false;
  // FormData / Blob / File / URLSearchParams / ArrayBuffer all carry their own tag.
  if (Object.prototype.toString.call(data) !== '[object Object]') return false;
  const proto = Object.getPrototypeOf(data);
  return proto === Object.prototype || proto === null;
}

/** The path after the API base, lower-cased, without query/hash or surrounding slashes. */
function apiPath(rest) {
  return rest.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '').toLowerCase();
}

// STAFF_GET_ROUTES → one anchored regex per route; `{param}` = exactly one non-empty path segment.
// Lower-cased on both sides (the path was lower-cased by apiPath). Built once, on first GET.
let staffGetMatchers = null;
function isStaffGetRoute(rest) {
  if (!staffGetMatchers) {
    staffGetMatchers = STAFF_GET_ROUTES.map((route) => new RegExp('^' + route.toLowerCase().split('/').map((seg) => (
      /^\{[^}]+\}$/.test(seg) ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    )).join('/') + '$'));
  }
  const p = apiPath(rest);
  return staffGetMatchers.some((re) => re.test(p));
}

/**
 * Decide for one axios request config (as a request interceptor sees it: method lower-cased, url
 * possibly relative to baseURL, headers not yet flattened).
 *   ctx = { apiBase, token, expiresAt (epoch ms), now (epoch ms), signedIn }
 * Returns { attach: boolean, reason: string }.
 */
export function decideAuthHeader(config, ctx) {
  const c = config || {};
  const x = ctx || {};
  const method = String(c.method || 'get').toLowerCase();

  if (!x.signedIn) return { attach: false, reason: 'no_user' };
  if (typeof x.token !== 'string' || !x.token || x.token === 'undefined' || x.token === 'null') {
    return { attach: false, reason: 'no_token' };
  }
  if (typeof x.expiresAt !== 'number' || !isFinite(x.expiresAt) || typeof x.now !== 'number'
      || x.now >= x.expiresAt - EXPIRY_SKEW_MS) {
    return { attach: false, reason: 'token_expiring' };
  }

  const apiBase = normalizeApiBase(x.apiBase);
  if (!apiBase) return { attach: false, reason: 'no_api_base' };

  const url = c.url;
  if (typeof url !== 'string' || !url) return { attach: false, reason: 'no_url' };
  let rest;
  if (ABSOLUTE_URL.test(url)) {
    if (url === apiBase) rest = '';
    else if (url.startsWith(apiBase + '/') || url.startsWith(apiBase + '?')) rest = url.slice(apiBase.length);
    else return { attach: false, reason: 'not_api' };
  } else {
    if (/^(blob|data|javascript|mailto|tel):/i.test(url)) return { attach: false, reason: 'not_api' };
    if (normalizeApiBase(c.baseURL) !== apiBase) return { attach: false, reason: 'not_api' };
    rest = url;
  }
  if (LOGIN_PATHS.includes(apiPath(rest))) return { attach: false, reason: 'login_route' };

  if (method === 'post') {
    if (!isPlainJsonBody(c.data)) return { attach: false, reason: 'simple_post' };
    const ct = headerValue(c.headers, 'content-type');
    if (ct !== undefined && !/^application\/json\b/i.test(String(ct))) return { attach: false, reason: 'simple_post' };
  } else if (method === 'get') {
    if (!isStaffGetRoute(rest)) return { attach: false, reason: 'get_not_staff' };
  } else if (method !== 'put' && method !== 'patch' && method !== 'delete') {
    return { attach: false, reason: 'method' };
  }

  if (callerSetAuthorization(c.headers, method)) return { attach: false, reason: 'caller_set' };

  return { attach: true, reason: 'attach' };
}

/**
 * Would this request carry the token IF a user were signed in with a fresh token? Used by the bounded session wait
 * (src/utils/authSessionGate.js, phase 4): only a request the policy would tag is worth holding for the session.
 * Same rule as decideAuthHeader, probed with a stand-in token, so the two can never disagree. Never throws.
 */
export function wouldAttachIfSignedIn(config, apiBase) {
  try {
    const now = Date.now();
    return decideAuthHeader(config, { apiBase, token: 'probe', expiresAt: now + 3600e3, now, signedIn: true }).attach === true;
  } catch (_e) {
    return false;
  }
}

/**
 * The request-interceptor body. Always returns the SAME config object (anything holding it — error.config,
 * retries — sees one object). When attaching, `config.headers` is REPLACED by a shallow copy carrying the
 * token, so no shared header object (axios.defaults.headers, a caller's reused headers) is ever written.
 * Never throws: on any surprise the request goes out exactly as it would today.
 */
export function applyAuthHeader(config, ctx) {
  try {
    if (!decideAuthHeader(config, ctx).attach) return config;
    config.headers = Object.assign({}, config.headers, { Authorization: 'Bearer ' + ctx.token });
    return config;
  } catch (_e) {
    return config;
  }
}
